// Vereinfachte API-Konfiguration basierend auf zentraler Umgebungskonfiguration
import { getApiBaseUrl, buildApiUrl, environment } from './environment';

interface ApiConfig {
  baseUrl: string;
  endpoints: {
    restApi: string;
    guests: string;
    bookings: string;
    calendar: string;
  };
  isDevelopment: boolean;
}

// Zentrale API-Konfiguration
export const apiConfig: ApiConfig = {
  baseUrl: getApiBaseUrl(),
  endpoints: {
    restApi: '/rest/api',        // RestApiController für documents, adverts, users, bookings
    guests: '/guests/api',       // GuestController
    bookings: '/bookings/api',   // BookingController
    calendar: '/calendar/api'    // CalendarController
  },
  isDevelopment: environment.DEBUG
};

// Helper function to construct TYPO3 API URLs
export const getApiUrl = (resource: string, action?: string, id?: string): string => {
  const baseUrl = `${getApiBaseUrl()}/?type=8847379247`; // RestApiController typeNum
  
  if (action && id) {
    return `${baseUrl}&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=${action}&tx_buchungsmanager_frontend[id]=${id}`;
  } else if (action) {
    if (id) {
      return `${baseUrl}&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=${action}&tx_buchungsmanager_frontend[id]=${id}`;
    } else {
      return `${baseUrl}&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=${action}`;
    }
  } else {
    // Resource-zu-Action Mapping
    const resourceActionMap: { [key: string]: string } = {
      'users': 'users',
      'invoices': 'documents',
      'guests': 'guests',
      'senders': 'senders',
      'templates': 'senders',
      'adverts': 'adverts',
      'bookings': 'bookings',
      'documents': 'documents',
      'additionalProducts': 'additionalProducts',
      'guestmap': 'saveGuestmap'
    };
    
    const action = resourceActionMap[resource] || 'users';
    return `${baseUrl}&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=${action}`;
  }
};

// Vereinfachte API-Endpunkt-Konfiguration
export const API_CONFIG = {
  // Users/Guests
  users: {
    list: () => getApiUrl('users'),
    get: (id: string) => getApiUrl('users', 'user', id),
    create: () => getApiUrl('users', 'createUser'),
    update: (id: string) => getApiUrl('users', 'updateUser', id),
    delete: (id: string) => getApiUrl('users', 'deleteUser', id)
  },
  
  // Guestmap
  guestmap: {
    save: () => getApiUrl('guestmap', 'saveGuestmap'),
    getActive: () => getApiUrl('guestmap', 'getActiveGuestmaps'),
    getTemplates: () => getApiUrl('guestmap', 'getGuestmapTemplates')
  },

  
  // Invoices/Documents
  invoices: {
    list: () => getApiUrl('invoices', 'frontend-documents'),
    get: (id: string) => getApiUrl('invoices', 'frontend-document', id),
    create: () => getApiUrl('invoices', 'create-frontend-document'),
    update: (id: string) => getApiUrl('invoices', 'update-frontend-document', id),
    delete: (id: string) => getApiUrl('invoices', 'create-frontend-document', id)
  },
  
  // Documents (Frontend)
  documents: {
    list: () => getApiUrl('documents'),
    get: (id: string) => getApiUrl('documents', 'document', id),
    create: () => getApiUrl('documents', 'create-frontend-document'),
    update: (id: string) => getApiUrl('documents', 'update-frontend-document', id),
    delete: (id: string) => getApiUrl('documents', 'delete-frontend-document', id)
  },
  
  // Senders
  senders: {
    list: () => getApiUrl('senders'),
    get: (id: string) => getApiUrl('senders', 'advert', id),
    create: () => getApiUrl('senders', 'createAdvert'),
    update: (id: string) => getApiUrl('senders', 'updateAdvert', id),
    delete: (id: string) => getApiUrl('senders', 'deleteAdvert', id)
  },
  
  // Templates
  templates: {
    list: () => getApiUrl('templates'),
    get: (id: string) => getApiUrl('templates', 'advert', id),
    create: () => getApiUrl('templates', 'createAdvert'),
    update: (id: string) => getApiUrl('templates', 'updateAdvert', id),
    delete: (id: string) => getApiUrl('templates', 'deleteAdvert', id)
  },
  
  // Guests
  guests: {
    list: () => getApiUrl('guests'),
    get: (id: string) => getApiUrl('guests', 'user', id),
    create: () => getApiUrl('guests', 'createGuest'),
    update: (id: string) => getApiUrl('guests', 'updateGuest', id),
    delete: (id: string) => getApiUrl('guests', 'deleteGuest', id)
  }
};

// Vollständige REST API URL mit korrektem type-Parameter
export const getFullApiUrl = (): string => {
  return `${getApiBaseUrl()}/?type=8847379247`;
};

// Debug-Ausgabe der aktuellen Konfiguration
export const debugApiConfig = (): void => {
  if (environment.DEBUG && typeof window !== 'undefined') {
    console.group('🔧 API Endpunkt-Konfiguration');
    console.log('Base URL:', apiConfig.baseUrl);
    console.log('REST API URL:', getFullApiUrl());
    console.log('Beispiel-URLs:');
    console.log('  - Guests List:', API_CONFIG.guests.list());
    console.log('  - Invoice Get:', API_CONFIG.invoices.get('123'));
    console.log('  - Document Create:', API_CONFIG.documents.create());
    console.groupEnd();
  }
};

// Test-Funktion für API-Verbindung
export const testApiConnection = async (): Promise<void> => {
  if (typeof window === 'undefined') return;

  const testEndpoints = [
    { name: 'Guests List', url: API_CONFIG.guests.list() },
    { name: 'Invoices List', url: API_CONFIG.invoices.list() },
    { name: 'Documents List', url: API_CONFIG.documents.list() },
    { name: 'Senders List', url: API_CONFIG.senders.list() }
  ];

  console.group('🧪 API Connection Test');

  for (const endpoint of testEndpoints) {
    console.log(`Testing ${endpoint.name}: ${endpoint.url}`);

    try {
      const response = await fetch(endpoint.url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      console.log(`✅ ${endpoint.name} Status:`, response.status);

      if (response.ok) {
        const data = await response.text();
        console.log(`✅ ${endpoint.name} Data:`, data.substring(0, 200) + '...');
      } else {
        console.error(`❌ ${endpoint.name} Failed:`, response.status, response.statusText);
      }

    } catch (error) {
      console.error(`❌ ${endpoint.name} Error:`, error);
    }
  }

  console.groupEnd();
};

// Automatisches Debug-Logging
if (environment.DEBUG) {
  debugApiConfig();

  // Test-Funktion global verfügbar machen
  if (typeof window !== 'undefined') {
    (window as any).testApiConnection = testApiConnection;
    console.log('💡 Tipp: Führe testApiConnection() in der Konsole aus');
  }
}

export default API_CONFIG; 